import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Star, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import SectionBand from '@/components/ui/SectionBand';

// Card shell that mirrors the rest of the Reserve dashboard surfaces (Balance,
// Reserved Flights, Activity, FAQ) so this no longer reads as a separate
// product. We don't reuse `paymentCardGradient.container` here because that
// preset bundles its own padding/border that conflicted with the SectionBand
// header — the SectionBand needs to live flush against the card edge.
const RESERVE_CARD_SHELL =
  'rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 ' +
  'shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] overflow-hidden h-full min-h-[200px] flex flex-col';

const CARD_BRANDS = {
  visa: { name: 'Visa', color: 'text-brand-400' },
  mastercard: { name: 'Mastercard', color: 'text-orange-400' },
  amex: { name: 'Amex', color: 'text-brand-300' },
  discover: { name: 'Discover', color: 'text-orange-300' },
  diners: { name: 'Diners', color: 'text-gray-400' },
  jcb: { name: 'JCB', color: 'text-green-400' },
  unionpay: { name: 'UnionPay', color: 'text-red-400' },
};

export default function PaymentMethodsManager({ 
  paymentMethods, 
  defaultPaymentMethod,
  isLoading,
  /** When false, server has no Stripe secret key — cards cannot load or save. */
  stripePaymentsAvailable = true,
  /** Stripe customer exists but listing failed (e.g. test/live key mismatch). */
  paymentMethodsError = false,
  /** From API: { type, code, message, param, statusCode } — actual Stripe error. */
  stripeLoadError = null,
  onRefresh,
  onAddCard,
  onSetDefault,
  onRemoveCard
}) {
  const [removingId, setRemovingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  const handleSetDefault = async (paymentMethodId) => {
    setSettingDefaultId(paymentMethodId);
    try {
      await onSetDefault(paymentMethodId);
      toast.success('Default card updated');
    } catch (error) {
      toast.error(error.message || 'Failed to set default card');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleRemove = async (paymentMethodId) => {
    setRemovingId(paymentMethodId);
    try {
      await onRemoveCard(paymentMethodId);
      toast.success('Card removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove card');
    } finally {
      setRemovingId(null);
    }
  };

  const getBrandInfo = (brand) => {
    return CARD_BRANDS[brand?.toLowerCase()] || { name: brand || 'Card', color: 'text-white' };
  };

  if (isLoading) {
    return (
      <div className={RESERVE_CARD_SHELL}>
        <SectionBand
          icon={CreditCard}
          iconColor="text-brand-300"
          accentRgb="34,211,238"
          title="Cards on File"
        />
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      </div>
    );
  }

  return (
    <div className={RESERVE_CARD_SHELL}>
      <SectionBand
        icon={CreditCard}
        iconColor="text-brand-300"
        accentRgb="34,211,238"
        title="Cards on File"
        rightSlot={
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddCard}
            disabled={!stripePaymentsAvailable}
            className="h-7 px-2.5 text-[11px] rounded-full bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/15 disabled:opacity-40 disabled:pointer-events-none"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Card
          </Button>
        }
      />

      <div className="flex-1 flex flex-col p-6 sm:p-8 pt-5 sm:pt-6">
      {!stripePaymentsAvailable ? (
        <div className="flex-1 rounded-xl border border-brand-500/30 bg-brand-500/10 px-4 py-4 text-left">
          <p className="text-brand-100/95 text-sm font-normal leading-relaxed">
            Saved cards are unavailable because Stripe is not configured on the app server. Add{' '}
            <code className="text-xs bg-black/30 px-1.5 py-0.5 rounded">STRIPE_SECRET_KEY</code> (secret{' '}
            <code className="text-xs">sk_…</code>) to your Vercel project environment for Production, then redeploy.
          </p>
        </div>
      ) : paymentMethodsError ? (
        <div className="flex-1 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-4 text-left space-y-3">
          {stripeLoadError?.type === 'StripeConnectionError' ? (
            <>
              <p className="text-orange-100/95 text-sm font-normal leading-relaxed">
                The server could not complete a connection to Stripe after several attempts. This is a{' '}
                <span className="text-orange-50/95">network reachability</span> issue between your hosting (e.g. Vercel)
                and Stripe—not a wrong saved card or test/live customer mismatch. Check{' '}
                <a
                  href="https://status.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-orange-200 hover:text-white"
                >
                  status.stripe.com
                </a>
                , try again in a few minutes, or retry below.
              </p>
              <p className="text-orange-100/70 text-xs font-normal leading-relaxed">
                If this never resolves, confirm <code className="text-[11px] bg-black/30 px-1 rounded">STRIPE_SECRET_KEY</code>{' '}
                on Vercel has no extra spaces or line breaks, and review Vercel function logs for TLS/DNS errors.
              </p>
            </>
          ) : (
            <p className="text-orange-100/95 text-sm font-normal leading-relaxed">
              Stripe could not load saved cards. Check the details below and your Vercel{' '}
              <code className="text-xs bg-black/30 px-1 rounded">STRIPE_SECRET_KEY</code> (must match the mode you use in
              the Stripe Dashboard).
            </p>
          )}
          {typeof onRefresh === 'function' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onRefresh()}
              className="border-orange-400/40 text-orange-100 hover:bg-orange-500/20 rounded-full"
            >
              Retry loading cards
            </Button>
          )}
          {stripeLoadError && (
            <details className="text-left">
              <summary className="text-orange-200/90 text-xs cursor-pointer select-none">
                Error details (for support / debugging)
              </summary>
              <pre className="mt-2 text-[11px] leading-snug text-orange-100/80 whitespace-pre-wrap break-words font-mono bg-black/25 rounded-lg p-3 overflow-x-auto border border-orange-500/20">
                {JSON.stringify(stripeLoadError, null, 2)}
              </pre>
            </details>
          )}
        </div>
      ) : paymentMethods.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-0">
          <CreditCard className="w-10 h-10 sm:w-12 sm:h-12 text-white/20 mb-3 sm:mb-4" />
          <p className="text-white/60 font-normal mb-3 sm:mb-4 text-sm sm:text-base">No cards saved yet</p>
          <Button
            onClick={onAddCard}
            className="bg-white text-black hover:bg-white/90 rounded-full h-9 sm:h-10 text-sm sm:text-base px-4"
          >
            Add Your First Card
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {paymentMethods.map((pm) => {
            const brandInfo = getBrandInfo(pm.brand);
            const isDefault = pm.id === defaultPaymentMethod || pm.is_default;

            return (
              <div
                key={pm.id}
                className={`flex flex-wrap items-center justify-between gap-2 p-4 rounded-xl border transition-all duration-200 ${
                  isDefault ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                  <div className={`font-medium text-sm sm:text-base ${brandInfo.color}`}>
                    {brandInfo.name}
                  </div>
                  <div className="text-white/85 text-sm sm:text-base">
                    •••• {pm.last4}
                  </div>
                  <div className="text-white/40 text-xs sm:text-sm">
                    {String(pm.exp_month).padStart(2, '0')}/{String(pm.exp_year).slice(-2)}
                  </div>
                  {isDefault && (
                    <span className="flex items-center gap-1 text-xs text-white/60 bg-white/10 px-2 py-1 rounded-full">
                      <Star className="w-3 h-3 fill-current flex-shrink-0" />
                      Default
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(pm.id)}
                      disabled={settingDefaultId === pm.id}
                      className="text-white/60 hover:text-white hover:bg-white/10 rounded-full text-xs"
                    >
                      {settingDefaultId === pm.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        'Set Default'
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(pm.id)}
                    disabled={removingId === pm.id}
                    className="text-red-400/60 hover:text-red-400 hover:bg-red-400/10 rounded-full p-2"
                  >
                    {removingId === pm.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
